import IconsEnum from '@enums/icons.enum'
import { Campaign, NumbersHelper, Tag, UserInDetails } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './can-receive.component.scoped.scss'

interface CanReceivePayment {
  type: string
  value: string | number
  term: string
  agentUsername?: string
}
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
  user: UserInDetails
}

export default ({ campaign, user, className, ...props }: Props) => {
  const { t } = useTranslation('canReceive')
  const iconsEnum = useMemo(() => IconsEnum, [])
  const numbersHelper = useMemo(() => new NumbersHelper(), [])
  const [listPayments, setListPayments] = useState<Array<CanReceivePayment>>([])

  const handlePayments = useCallback(() => {
    let list: Array<CanReceivePayment> = []
    if (user?.scope?.payments?.length > 0) {
      list = user?.scope?.payments.map((pay) => ({
        type: pay?.paymentType,
        value: pay?.value || pay?.description,
        term: pay?.term,
        agentUsername: pay?.agentUsername,
      }))
    } else if (user?.customPayment) {
      list.push({ type: 'money', value: user?.customPayment, term: null })
    } else if (user?.status !== 'inContact') {
      let payments = campaign?.activation?.payments
      if (campaign?.activation?.suggestion) {
        if (!user || (user && !['inContact', 'recruited'].includes(user?.status))) {
          payments = campaign?.activation?.suggestionPayments
        }
      }
      if (payments) {
        Object.keys(payments).forEach(function (key) {
          if (payments[key]) {
            list.push({ type: key, value: payments[key], term: null })
          }
        })
      }
    }
    return list
  }, [campaign, user])

  useEffect(() => {
    const payments = handlePayments()
    if (payments) {
      setListPayments(payments)
    }
  }, [handlePayments])

  if (!listPayments.length) {
    return null
  }

  return (
    <section {...props} className={`${className} can-receive`}>
      <div className="title">
        <h5>{t('title')}</h5>
      </div>
      <div className="tags">
        {listPayments?.map((payment, i) => (
          <div className={`${payment?.type} mb-5`} key={`${payment?.type}-${i}`}>
            {payment?.type === 'agent' ? (
              <div>
                <Tag labels={[<span className="label-text text-bold not-shrink">{t(`payment.${payment?.type}.consultancy`)}</span>]} />
                <Tag
                  labels={[
                    <span className="label-text not-shrink">{t(`payment.${payment?.type}.responsibleConsultancy`)}</span>,
                    <strong className="label-text">@{payment?.agentUsername}</strong>,
                  ]}
                />
              </div>
            ) : null}
            <Tag
              icon={
                iconsEnum?.[payment?.type] ? (
                  <i className={`${iconsEnum[payment?.type]}`} style={{ display: 'flex', flexShrink: 0 }}></i>
                ) : null
              }
              labels={[
                <span className="label-text not-shrink">{t(`payment.${payment?.type}.label`)}</span>,
                <strong className="label-text">
                  {['cash', 'agent'].includes(payment?.type) ? numbersHelper.convertMoneyValue(payment?.value) : payment?.value}
                </strong>,
              ]}
              key={`tag-${payment?.type}-${i}`}
              separator
            />
            {payment?.term ? (
              <Tag
                icon={<i className="fa-light fa-calendar" style={{ display: 'flex', flexShrink: 0 }}></i>}
                key={`term-${payment?.type}-${i}`}
                labels={[
                  <span className="label-text not-shrink">{t(`payment.${payment?.type}.termLabel`)}</span>,
                  <strong className="label-text">
                    {t(`payment.${payment?.type}.termValue.days.label`, {
                      count: Number(payment?.term),
                    })}
                  </strong>,
                ]}
                separator
              />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}
